/**
 * Hook to check feature status from backend
 * Polls every 30 seconds to detect admin changes (including maintenance mode)
 */

import { useState, useEffect, useCallback } from 'react';
import { useVisibility } from '@/lib/hooks/useVisibility';

// Client-side fallback for the chat empty-state suggestion chips, used only when
// the /api/config/features fetch fails. The live list is admin-editable
// (AdminConfig AGENT_CHAT_SUGGESTIONS) and arrives in features.chatSuggestions;
// an admin-saved empty array is respected (no chips).
export const DEFAULT_CHAT_SUGGESTIONS: string[] = [
  'Generate a neon cyberpunk city at night',
  'Turn my photo into an oil painting',
  'Make a 5-second looping video of ocean waves',
  'Create a minimalist logo for a coffee brand',
];

// Client-side fallback for the post-upload chips (AdminConfig AGENT_UPLOAD_SUGGESTIONS).
export const DEFAULT_UPLOAD_SUGGESTIONS: string[] = [
  'Remove her clothes',
  'Make it more revealing',
  'Change the background',
  'Turn this into a video',
];

interface FeatureStatus {
  imageGeneration: boolean;
  videoGeneration: boolean;
  chatGeneration: boolean;
  chatSuggestions: string[];
  uploadSuggestions: string[];
}

interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
}

export function useFeatureStatus() {
  const [features, setFeatures] = useState<FeatureStatus>({
    imageGeneration: true, // Default to enabled for graceful degradation
    videoGeneration: true,
    chatGeneration: true,
    chatSuggestions: DEFAULT_CHAT_SUGGESTIONS,
    uploadSuggestions: DEFAULT_UPLOAD_SUGGESTIONS,
  });
  const [maintenance, setMaintenance] = useState<MaintenanceStatus>({
    enabled: false,
    message: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isVisible = useVisibility();

  const fetchFeatureStatus = useCallback(async () => {
    try {
      const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.ultragoon.co';
      const response = await fetch(`${API_URL}/api/config/features`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch feature status');
      }

      const data = await response.json();
      
      if (data.success && data.features) {
        setFeatures({
          imageGeneration: data.features.imageGeneration ?? true,
          videoGeneration: data.features.videoGeneration ?? true,
          // `chat` is the master agent-chat availability flag (see getFeatureStatus).
          chatGeneration: data.features.chat ?? true,
          // Admin-editable suggestion chips. An explicit [] means "show none" and
          // is respected; only a missing/non-array value falls back to defaults.
          chatSuggestions: Array.isArray(data.features.chatSuggestions)
            ? data.features.chatSuggestions
            : DEFAULT_CHAT_SUGGESTIONS,
          uploadSuggestions: Array.isArray(data.features.uploadSuggestions)
            ? data.features.uploadSuggestions
            : DEFAULT_UPLOAD_SUGGESTIONS,
        });
        setMaintenance({
          enabled: data.maintenance?.enabled ?? false,
          message: data.maintenance?.message ?? null,
        });
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch feature status:', err);
      setError('Failed to load feature status');
      // Keep defaults (enabled) on error for graceful degradation
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch and poll when tab is visible
    if (!isVisible) return;

    // Fetch on mount (or when tab becomes visible again)
    fetchFeatureStatus();

    // Poll every 30 seconds to detect admin changes (maintenance mode)
    const interval = setInterval(fetchFeatureStatus, 30 * 1000);

    return () => clearInterval(interval);
  }, [fetchFeatureStatus, isVisible]);

  return { 
    features,
    maintenance,
    loading, 
    error,
    refetch: fetchFeatureStatus, // Expose for manual refresh after errors
  };
}

